import { PathsV1VehicleSearchPostParametersQueryExtend } from "@/api-schema-enums";
import {
  resolveVehicleValuationErrorDescription,
  VEHICLE_VALUATION_CUSTOM_ERRORS,
} from "@/common/api/vehicle-valuation/_helpers/vehicle-valuation-error-description";
import {
  useVehicleValuationCreateMutation,
  type VehicleValuationCreateResponse,
} from "@/common/api/vehicle-valuation/use-vehicle-valuation-create.mutation";
import { useVehicleByVinQuery } from "@/common/api/vehicle/use-vehicle-by-vin.query";
import { VehicleWithRegistrationDate } from "@/common/api/vehicle/vehicle.types";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { normalizeVehicleVin } from "@/common/helpers/vehicle-vin";
import { ErrorPage } from "@/components/error-page/error-page";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { userStore } from "@/store/user-store";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import {
  VehicleValuationCreateFormFields,
  VehicleValuationCreateFormValues,
} from "./_components/vehicle-valuation-create-form-fields";
import { buildVehicleValuationCreateBody } from "./_helpers/vehicle-valuation-create-form.helpers";
import { getVehicleValuationRoute } from "../_helpers/get-vehicle-valuation-route";

export interface VehicleValuationCreateByVinViewProps {
  vin: string;
}

export function VehicleValuationCreateByVinView({ vin }: VehicleValuationCreateByVinViewProps) {
  const { t } = useTranslation(["common", "fab", "masterdata"]);
  const [_, navigate] = useLocation();
  const { user } = userStore();
  const normalizedVin = useMemo(() => normalizeVehicleVin(vin), [vin]);

  const {
    data: existingVehicle,
    isPending: vehicleIsPending,
    isError: vehicleHasError,
  } = useVehicleByVinQuery(normalizedVin, {
    parameters: {
      extend: [
        PathsV1VehicleSearchPostParametersQueryExtend.ModelGeneration,
        PathsV1VehicleSearchPostParametersQueryExtend.ModelGeneration_ModelFamily,
        PathsV1VehicleSearchPostParametersQueryExtend.ModelGeneration_ModelFamily_Brand,
        PathsV1VehicleSearchPostParametersQueryExtend.VehicleBlock,
        PathsV1VehicleSearchPostParametersQueryExtend.OwningBranch,
        PathsV1VehicleSearchPostParametersQueryExtend.OwningBranch_Company,
        PathsV1VehicleSearchPostParametersQueryExtend.OwningBranch_Brands,
        PathsV1VehicleSearchPostParametersQueryExtend.OwningBranch_Address,
        PathsV1VehicleSearchPostParametersQueryExtend.CurrentBranch,
        PathsV1VehicleSearchPostParametersQueryExtend.CurrentBranch_Company,
        PathsV1VehicleSearchPostParametersQueryExtend.CurrentBranch_Brands,
        PathsV1VehicleSearchPostParametersQueryExtend.CurrentBranch_Address,
        PathsV1VehicleSearchPostParametersQueryExtend.ExchangeEngine,
      ],
    },
  });

  const vehicle = existingVehicle as VehicleWithRegistrationDate | null;

  const { isPending: vehicleValuationCreateIsLoading, mutateAsync: vehicleValuationCreateMutateAsync } =
    useVehicleValuationCreateMutation();

  const onSubmit = async (values: VehicleValuationCreateFormValues) => {
    let createdValuation: VehicleValuationCreateResponse | null = null;

    await toastMutationStatus({
      mutationFn: async () => {
        createdValuation = await vehicleValuationCreateMutateAsync(
          buildVehicleValuationCreateBody(values, {
            requester: user.uuid,
            vin: normalizedVin,
          }),
        );

        return createdValuation;
      },
      successMessage: t("fab:VehicleValuationCreated"),
      errorMessage: t("fab:ErrorCreatingVehicleValuation"),
      customErrors: VEHICLE_VALUATION_CUSTOM_ERRORS,
      resolveErrorMessage: resolveVehicleValuationErrorDescription,
    });

    if (createdValuation?.uuid) {
      navigate(getVehicleValuationRoute(createdValuation));
    }
  };

  if (vehicleHasError) {
    return (
      <ErrorPage
        title={t("masterdata:VehicleNotFound")}
        message={t("masterdata:VehicleNotFoundForVin", { vin: normalizedVin })}
        backTo="~/fab/list/initialize/select-vehicle"
      />
    );
  }

  return (
    <CenteredLayout
      variant={CenteredLayoutVariant.Form}
      title={t("fab:CreateVehicleValuation")}
      subTitle={t("fab:CreateVehicleValuationByVinDescription")}
    >
      <VehicleValuationCreateFormFields
        vehicle={vehicle}
        defaultVin={normalizedVin}
        showVinField={true}
        vinReadOnly={true}
        requireRegistrationDate
        isPending={vehicleIsPending || vehicleValuationCreateIsLoading}
        onCancel={() => navigate("~/fab/list/initialize/select-vehicle")}
        onSubmit={onSubmit}
      />
    </CenteredLayout>
  );
}
